// Toasts + a polite live region for screen-reader announcements. Neither steals focus
// (RF-UI-15). `announce` is for realtime changes worth hearing; toasts for action results.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useT } from "../i18n/index.tsx";
import { IconButton } from "./Button.tsx";
import { CheckCircleIcon, InfoIcon, XCircleIcon, XIcon } from "./icons.tsx";

type ToastTone = "success" | "error" | "info";
interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  toast: (message: string, tone?: ToastTone) => void;
  announce: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [items, setItems] = useState<ToastItem[]>([]);
  const [announcement, setAnnouncement] = useState("");
  const seq = useRef(0);
  const timers = useRef(new Set<number>());
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; timers.current.forEach(timer => window.clearTimeout(timer)); timers.current.clear(); };
  }, []);
  const later = useCallback((callback: () => void, delay: number) => {
    if (!alive.current) return;
    const timer = window.setTimeout(() => { timers.current.delete(timer); if (alive.current) callback(); }, delay);
    timers.current.add(timer);
  }, []);

  const dismiss = useCallback((id: number) => setItems((list) => list.filter((i) => i.id !== id)), []);

  const toast = useCallback(
    (message: string, tone: ToastTone = "info") => {
      if (!alive.current) return;
      const id = ++seq.current;
      setItems((list) => [...list.slice(-3), { id, tone, message }]);
      later(() => dismiss(id), tone === "error" ? 9000 : 5000);
    },
    [dismiss, later],
  );

  const announce = useCallback((message: string) => {
    if (!alive.current) return;
    // Clear first so repeating the same text is announced again.
    setAnnouncement("");
    later(() => setAnnouncement(message), 50);
  }, [later]);

  const api = useMemo(() => ({ toast, announce }), [toast, announce]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="sr-only" aria-live="polite" aria-atomic="true" data-testid="live-region">
        {announcement}
      </div>
      <div className="toast-region" role="status" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={`toast toast--${item.tone}`}>
            {item.tone === "success" ? <CheckCircleIcon /> : item.tone === "error" ? <XCircleIcon /> : <InfoIcon />}
            <p>{item.message}</p>
            <IconButton label={t("common.dismiss")} onClick={() => dismiss(item.id)} style={{ width: 28, height: 28 }}>
              <XIcon size={14} />
            </IconButton>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
